import type { Direction, Point } from "@/core/types";
import type { SurvivalPressureProfile } from "@/levels/types";

/**
 * PR26: which GLOBAL bite/maneuver scoring CONSTANTS (`GameConfig.bites`/
 * `GameConfig.maneuvers` - RUSH's combo table, LAST-SECOND TURN's trigger/
 * reward, SPEED TRIAL's reward, and any future retune of the same kind) a
 * Daily result's raw `score` was computed under. Deliberately independent
 * of both `generatorVersion` (board/generator mechanics, frozen once per
 * CALENDAR DAY at spec-generation time via `DAILY_VERSION_RULES` in
 * `convert.ts`) and `scoringProfile.id` (that day's own Daily Points
 * COEFFICIENTS) - neither of those can ever change mid-day, but a client
 * deploy (and therefore `GameConfig`) can, at any time, independent of
 * when a date's Daily Challenge spec was generated. Without this, a
 * player who submits before a scoring-constants deploy and one who plays
 * the SAME still-open Daily date after it would have their raw `score`
 * (and therefore Daily Points, which folds `score` in directly - see
 * `dailyPoints.ts`) computed under two incompatible rulesets while
 * ranked together on the exact same `challenge_id` leaderboard.
 *
 * Submitted with every result (`DailyResultSubmission.scoringRulesVersion`
 * - see `validation.ts`), stored per participant as
 * `daily_participation.best_rules_version`, and used by
 * `get_daily_leaderboard()`/`get_my_daily_rank()` (see
 * `supabase/migrations/012_daily_scoring_rules_version.sql`) to rank a
 * participant only against others sharing the SAME version - a version
 * mismatch against a participant's own stored best is treated exactly
 * like "no existing best" (see `dailyRanking.ts`'s
 * `shouldBecomeDailyBest()`), so a new submission always establishes a
 * fresh best rather than being silently compared against an
 * incompatible old one. Historical rows are never deleted or rewritten -
 * a legacy row with no version at all (`best_rules_version is null`, i.e.
 * every result recorded before this field existed) is treated as version
 * `1`, and this PR is the first to bump it, exactly mirroring
 * `ENDLESS_RULES_VERSION`'s own "1 -> 2" reasoning in
 * `src/endless/types.ts`. Bump this again whenever a future PR changes
 * any global bite/maneuver scoring constant Daily's `score` reflects -
 * never `generatorVersion` (that's for generator MECHANICS, not scoring
 * constants - see `DAILY_VERSION_RULES`'s own doc for exactly that
 * distinction) and never `scoringProfile.id` (that's a per-day
 * COEFFICIENT choice, not a global constants regime).
 */
export const DAILY_SCORING_RULES_VERSION = 2;

/**
 * V1's map family labels - kept only so a stored `formatVersion: 1` row
 * (generated before this Generator V2 redesign) can still be read/typed
 * correctly. `generateDailyChallenge()` never produces a new V1 spec
 * going forward - see `generator.ts`'s version dispatch and
 * `docs`/README's "V1 stored rows" section.
 */
export type DailyMapFamily = "open_arena" | "corridors" | "islands" | "rings" | "chambers" | "pinch_points";

/**
 * V2's layout archetypes (Generator V2 - see `src/daily/layouts/`). Each
 * is a genuinely distinct obstacle-placement algorithm, not a
 * rotation/reflection of another - `DAILY_TRANSFORMS` (rotation/reflection)
 * still applies on top of every one of these for additional day-to-day
 * variety, but is never itself what makes two days count as different
 * archetypes.
 */
export type DailyLayoutArchetype =
  | "sparse_open_field"
  | "asteroid_field"
  | "twin_lanes"
  | "zigzag_gauntlet"
  | "crossroads"
  | "broken_ring"
  | "double_ring"
  | "spiral"
  | "four_chambers"
  | "offset_chambers"
  | "hourglass_pinch"
  | "asymmetric_archipelago";

/**
 * V1's supported CHOOSE YOUR BITE restrictions - unchanged in V2.
 * `standard` is the normal SAFE/RUSH/CHAOS trio, unchanged. The `*_only`
 * modes restrict every fruit group to a single kind - see `LevelGame`'s
 * `allowedBiteKinds` option and `biteSpawn.ts`'s `buildKindsForSpawn()`.
 */
export type DailyBiteMode = "standard" | "safe_only" | "rush_only" | "chaos_only";

/**
 * V1's optional flavor modifiers. Superseded by V2's recipe system
 * (`DailyRecipeId`) as the primary source of day-to-day identity, but
 * kept as a type (and still settable on a V2 spec) since they're still
 * meaningful concrete parameter nudges a recipe can layer on top of its
 * own archetype/pressure/scoring choices - e.g. a "long_snake" starting
 * condition remains exactly what it always was.
 */
export type DailyModifier = "long_snake" | "sprint_target" | "high_speed" | "tight_navigation";

/** V2's curated recipe families - see `src/daily/recipes.ts` for each one's compatible ranges and the difficulty-budget rule that keeps them fair. */
export type DailyRecipeId =
  | "fast_corridor_gauntlet"
  | "long_snake_pinch_survival"
  | "open_chaos_arena"
  | "safe_growth_field"
  | "broken_ring_rush"
  | "dense_asteroid_endurance"
  | "balanced_chamber_challenge"
  | "spiral_score_attack";

/** V2's four scoring-profile families - see `src/daily/dailyPoints.ts` for the exact coefficients and the reasoning behind them. */
export type DailyScoringProfileId = "survival_heavy" | "growth_heavy" | "score_heavy" | "balanced";

/**
 * A challenge day's typed, versioned, transparent Daily Points formula -
 * stored inside the immutable spec so every player on that date (and the
 * backend, when verifying a submitted result) uses the exact same
 * coefficients. All-integer by construction (see `computeDailyPoints()`)
 * - there is no floating-point step anywhere in the formula.
 */
export interface DailyScoringProfile {
  id: DailyScoringProfileId;
  /** Display label, e.g. "SURVIVAL-HEAVY" - shown verbatim in the pre-run "TODAY'S SCORING" panel. */
  label: string;
  /** Points per final-length segment. Always a positive integer - every profile gives length a real, non-zero contribution. */
  lengthCoefficient: number;
  /** Points per whole active-survival second (`floor(activePlayMs / 1000)`). Always a positive integer. */
  survivalPointsPerSecond: number;
  /** Points per gameplay score point. Always a positive integer. */
  scoreCoefficient: number;
}

/**
 * Strongly-typed, versioned specification for one day's Daily Challenge.
 * Every player, every attempt, gets exactly this - generated once,
 * deterministically, and stored immutably (see
 * `netlify/functions/daily-challenge.ts` and
 * `supabase/migrations/004_daily_challenge.sql`'s
 * `get_or_create_daily_challenge()`). Framework-agnostic: this shape has
 * no Phaser/rendering concept in it at all, only what `LevelGame` (via
 * `dailyChallengeToLevelDefinition()`, see `convert.ts`) needs to run it,
 * plus the Daily-only display/scoring fields.
 *
 * `DailyChallengeSpecAny` is the real type read back from storage - a
 * union of `formatVersion: 1` (frozen forever, exactly the shape V1
 * generated - see `generator.ts`'s version dispatch) and
 * `formatVersion: 2` (this redesign: survival objective, layout
 * archetype/recipe, scoring profile, pressure profile). Consuming code
 * must always narrow on `formatVersion` before reading any V2-only field
 * - never assume a stored row is V2. See the README's "V1 stored rows"
 * section for exactly what happens when an old V1 date is loaded today.
 */
export interface DailyChallengeSpecV1 {
  formatVersion: 1;
  challengeId: string;
  generatorVersion: number;
  seed: number;

  mapFamily: DailyMapFamily;
  themeWorldId: string;

  grid: { cols: number; rows: number };
  snakeStart: Point;
  startDirection: Direction;
  startingLength: number;
  targetLength: number;
  baseStepMs: number;
  obstacles: Point[];
  exit: Point;

  biteMode: DailyBiteMode;
  modifiers: DailyModifier[];

  rngSeeds: {
    board: number;
    chaos: number;
  };
}

export interface DailyChallengeSpecV2 {
  formatVersion: 2;
  challengeId: string;
  generatorVersion: number;
  seed: number;

  /** Stable typed archetype id - see `DailyLayoutArchetype`'s doc. Never itself just a rotation/reflection label; `layoutVariant` below is what a rotation/reflection choice is recorded as. */
  layoutArchetype: DailyLayoutArchetype;
  /** Human-readable layout name shown on the pre-run screen, e.g. "TWIN LANES". */
  layoutName: string;
  /** Which of `DAILY_TRANSFORMS` was applied to the canonical archetype layout - recorded for audit/debugging (`src/daily/__scripts__/varietyAudit.ts`), never itself counted as a distinct archetype. */
  layoutVariant: string;
  recipeId: DailyRecipeId;
  themeWorldId: string;

  grid: { cols: number; rows: number };
  snakeStart: Point;
  startDirection: Direction;
  startingLength: number;
  /** Survival has no target length/gate - kept only as a defensive sentinel `LevelGame` never reads for a `formatVersion: 2` spec's `objective: "survival"` conversion (see `convert.ts`); never displayed, never validated as an achievability requirement (see `validator.ts`'s survival checks replacing the old one). */
  baseStepMs: number;
  obstacles: Point[];
  /** Kept only for `LevelDefinition`'s required shape / `LevelValidator`'s existing exit-reachability check - inert for a survival run (the gate can never open, see `LevelGame`'s `objective` guard), never rendered as a gate. */
  exit: Point;

  biteMode: DailyBiteMode;
  modifiers: DailyModifier[];

  scoringProfile: DailyScoringProfile;
  pressureProfile: SurvivalPressureProfile;

  /**
   * PR11 (Speed Engine V2) forensic marker, extended by PR12: which Speed
   * Engine version this spec's gameplay was generated to run under - `2`
   * for every spec produced by Generator V3 (`generatorVersion: 3`,
   * frozen), `3` for every spec produced by Generator V4
   * (`generatorVersion: 4`, PR12's growth/normal-cap tuning, the active
   * default). Absent (`undefined`) on any spec generated before this
   * field existed (every V1 spec, and every V2 spec with
   * `generatorVersion: 2`) - always treat it as "no speed engine version
   * recorded" when reading, never assume 1. Never used to reconstruct or
   * run a legacy speed formula - see `src/daily/generator.ts`'s module
   * doc for why V1/V2/V3 stay frozen instead. Stored inside this
   * already-flexible `jsonb` spec column - no database migration needed
   * for this field to exist.
   */
  speedEngineVersion?: number;

  rngSeeds: {
    board: number;
    chaos: number;
  };
}

export type { DailyChallengeSpecV3 } from "./v6/types";
import type { DailyChallengeSpecV3 } from "./v6/types";

/**
 * `formatVersion: 3` (Daily V6, PR25 - "the mixer of the whole game") is
 * its own TYPE SHAPE (tiers/archetypes/speed+pressure+pacing profiles/
 * fixed portals/BLACK HOLE - none of which V1/V2 have), so it gets its
 * own member of this union rather than reusing `formatVersion: 2` - see
 * `src/daily/v6/types.ts`'s module doc.
 */
export type DailyChallengeSpecAny = DailyChallengeSpecV1 | DailyChallengeSpecV2 | DailyChallengeSpecV3;

/** The current/active spec shape - what `generateDailyChallenge()` produces and what fresh V2 gameplay code should generally type against. Kept as its own alias so most call sites don't have to spell out the union. */
export type DailyChallengeSpec = DailyChallengeSpecV2;
